import { useState } from 'react';
import type { PhotoDetail } from '@/types/photo';
import { formatFileSize, formatResolution } from '@/types/photo';
import { useAuth } from '@/hooks/useAuth';

interface PhotoInfoPanelProps {
  photo: PhotoDetail;
  onCaptionSave: (caption: string) => Promise<boolean>;
  onDelete?: () => void;
}

export default function PhotoInfoPanel({ photo, onCaptionSave, onDelete }: PhotoInfoPanelProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEdit = user && (user.email === photo.uploaderEmail || user.role === 'admin');
  const canDelete = user && (user.email === photo.uploaderEmail || user.role === 'admin');

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const ok = await onCaptionSave(caption);
    setSaving(false);
    if (ok) {
      setIsEditing(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    }
  };

  const handleCancel = () => {
    setCaption(photo.caption);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (deleting || !onDelete) return;
    setDeleting(true);
    onDelete();
  };

  return (
    <div className="bg-background-50 rounded-t-2xl -mt-5 relative z-30">
      {/* Handle */}
      <div className="flex justify-center pt-2 pb-1">
        <div className="w-8 h-1 rounded-full bg-background-300/60"></div>
      </div>

      {/* Caption */}
      <div className="px-4 pb-3">
        {isEditing ? (
          <div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full bg-secondary-50 rounded-xl p-3 text-sm text-foreground-800 resize-none border border-background-200/70 focus:border-accent-400 focus:outline-none transition-colors"
              rows={3}
              maxLength={500}
              placeholder="사진에 대한 이야기를 남겨보세요"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-foreground-400">{caption.length}/500</span>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-xs font-medium text-foreground-500 bg-background-100 rounded-lg hover:bg-secondary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !caption.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-background-50 bg-accent-500 rounded-lg hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {photo.caption ? (
                <p className="text-sm text-foreground-800 leading-relaxed">{photo.caption}</p>
              ) : (
                <p className="text-sm text-foreground-400 italic">사진에 대한 이야기를 남겨보세요</p>
              )}
            </div>
            {canEdit && (
              <button
                onClick={() => setIsEditing(true)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
                aria-label="설명 수정"
              >
                <i className="ri-edit-line text-foreground-500"></i>
              </button>
            )}
          </div>
        )}
        {showSaved && (
          <div className="mt-2 bg-accent-100 text-accent-600 text-xs px-3 py-1.5 rounded-lg animate-fade-in">
            설명이 저장되었습니다
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="px-4"><div className="border-t border-background-200/50"></div></div>

      {/* Photo Info */}
      <div className="px-4 py-3">
        <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2">사진 정보</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {photo.shotDate && (
            <InfoRow icon="ri-calendar-line" label="촬영일" value={photo.shotDate} />
          )}
          {photo.uploaderName && (
            <InfoRow icon="ri-user-line" label="올린 사람" value={photo.uploaderName} />
          )}
          {photo.originalFilename && (
            <InfoRow icon="ri-file-line" label="파일명" value={photo.originalFilename} />
          )}
          {photo.fileSize > 0 && (
            <InfoRow icon="ri-hard-drive-line" label="파일 크기" value={formatFileSize(photo.fileSize)} />
          )}
          {photo.width > 0 && photo.height > 0 && (
            <InfoRow icon="ri-aspect-ratio-line" label="해상도" value={formatResolution(photo.width, photo.height)} />
          )}
          {photo.fileType && (
            <InfoRow icon="ri-image-line" label="파일 형식" value={photo.fileType.replace('image/', '').toUpperCase()} />
          )}
        </div>
      </div>

      {/* Delete Button */}
      {canDelete && (
        <div className="px-4 pb-6 pt-1">
          <div className="px-4"><div className="border-t border-background-200/50 mb-3"></div></div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full py-3 text-sm font-medium text-red-500 bg-red-50 rounded-xl hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-delete-bin-line"></i>
            사진 삭제하기
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
                <i className="ri-alert-line text-xl text-red-500"></i>
              </div>
              <h3 className="text-base font-bold text-foreground-950 mb-1">사진 삭제</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                삭제한 사진은 휴지통으로 이동합니다.<br />
                정말 삭제하시겠어요?
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-background-200/70 text-sm font-medium text-foreground-600 bg-background-50 hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-background-50 bg-red-500 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? '삭제 중...' : '삭제하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-xs text-foreground-400`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-foreground-400 leading-tight">{label}</p>
        <p className="text-xs text-foreground-700 truncate">{value}</p>
      </div>
    </div>
  );
}